/**
 * Running a local relay server will allow you to hide your API key
 * and run custom logic on the server
 *
 * Set the local relay server address to:
 * REACT_APP_LOCAL_RELAY_SERVER_URL=http://localhost:8081
 *
 * This will also require you to set OPENAI_API_KEY= in a `.env` file
 * You can run it with `npm run relay`, in parallel with `npm start`
 */
const LOCAL_RELAY_SERVER_URL: string = process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || '';

import { useEffect, useRef, useCallback, useState } from 'react';
import axios from 'axios';
import { RealtimeClient } from '../lib/realtime-api-beta';
import { ItemType } from '../lib/realtime-api-beta/dist/lib/client.js';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { instructions } from '../utils/conversation_config.js';
import { WavRenderer } from '../utils/wav_renderer';
import { X, Edit, Zap, ArrowUp, ArrowDown } from 'react-feather';
import { Button } from '../components/button/Button';
import { Toggle } from '../components/toggle/Toggle';
import { Map } from '../components/Map';
import { Mic, MicOff } from 'lucide-react';
import './ConsolePage.scss';

/**
 * Type for result from get_weather() function call
 */
interface Coordinates {
  lat: number;
  lng: number;
  location?: string;
  temperature?: {
    value: number;
    units: string;
  };
  wind_speed?: {
    value: number;
    units: string;
  };
}

/**
 * Type for all event logs
 */
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

export function ConsolePage() {
  /**
   * Ask user for API Key
   * If we're using the local relay server, we don't need this
   */
  localStorage.setItem('tmp::voice_api_key', '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');
  const apiKey = LOCAL_RELAY_SERVER_URL
    ? ''
    : localStorage.getItem('tmp::voice_api_key') ||
      prompt('OpenAI API Key...') ||
      '';
  if (apiKey !== '') {
    localStorage.setItem('tmp::voice_api_key', apiKey);
  }

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(new WavStreamPlayer({ sampleRate: 24000 }));
  const clientRef = useRef<RealtimeClient>(
    new RealtimeClient(
      LOCAL_RELAY_SERVER_URL
        ? { url: LOCAL_RELAY_SERVER_URL }
        : {
          apiKey: apiKey,
          dangerouslyAllowAPIKeyInBrowser: true,
        }
    )
  );

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<{ [key: string]: boolean }>({});
  const [isConnected, setIsConnected] = useState(false);
  const [canPushToTalk, setCanPushToTalk] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>({});
  const [coords, setCoords] = useState<Coordinates | null>({ lat: 37.775593, lng: -122.418137 });
  const [marker, setMarker] = useState<Coordinates | null>(null);
  const [isPaused, setIsPaused] = useState(true);

  // Conjunto para rastrear ítems ya procesados
  const processedItemsRef = useRef<Set<string>>(new Set());

  /**
   * Utility for formatting the timing of logs
   */
  const formatTime = useCallback((timestamp: string) => {
    const startTime = startTimeRef.current;
    const t0 = new Date(startTime).valueOf();
    const t1 = new Date(timestamp).valueOf();
    const delta = t1 - t0;
    const hs = Math.floor(delta / 10) % 100;
    const s = Math.floor(delta / 1000) % 60;
    const m = Math.floor(delta / 60_000) % 60;
    const pad = (n: number) => {
      let s = n + '';
      while (s.length < 2) {
        s = '0' + s;
      }
      return s;
    };
    return `${pad(m)}:${pad(s)}.${pad(hs)}`;
  }, []);

  /**
   * When you click the API key
   */
  const resetAPIKey = useCallback(() => {
    const apiKey = prompt('OpenAI API Key');
    if (apiKey !== null) {
      localStorage.clear();
      localStorage.setItem('tmp::voice_api_key', apiKey);
      window.location.reload();
    }
  }, []);

  const togglePauseConversation = async () => {
    try {
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;

      if (isPaused) {
        // Reanudar grabación
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      } else {
        // Pausar grabación y forzar una respuesta del asistente
        await wavRecorder.pause();
        client.createResponse(); // Forzar al cliente a procesar el turno y generar una respuesta
      }

      setIsPaused(!isPaused);
    } catch (error) {
      setIsPaused(true);
      console.log('Error al pausar/reanudar la grabación:', error);
    }
  };

  /**
   * Connect to conversation:
   * WavRecorder takes speech input, WavStreamPlayer output, client is API client
   */
  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Set state variables
    startTimeRef.current = new Date().toISOString();
    setIsConnected(true);
    setRealtimeEvents([]);
    setItems(client.conversation.getItems());
    processedItemsRef.current.clear(); // Limpiar el conjunto al iniciar una nueva conversación

    // Connect to microphone
    await wavRecorder.begin();

    // Connect to audio output
    await wavStreamPlayer.connect();

    // Connect to realtime API
    await client.connect();
    client.sendUserMessageContent([
      {
        type: `input_text`,
        text: `Hola!`,
      },
    ]);

    try {
      if (client.getTurnDetectionType() === 'server_vad') {
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      }

      await changeTurnEndType('server_vad');

      await testSocketEndpoint();

      setIsPaused(false);
    } catch (error) {
      console.log('error en connectConversation');
    }
  }, []);

  const testSocketEndpoint = async () => {
    // URL del endpoint
    const url = 'https://api.openai.com/v1/realtime?model=gpt-realtime-2025-08-28';

    // Opciones para la solicitud
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    fetch(url, options)
      .then(response => {
        if (!response.ok) {
          console.log(`xxxError: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('Datos recibidos:', data);
      })
      .catch(error => {
        console.error('Error en la solicitud:', error);
      });
  };

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);
    setRealtimeEvents([]);
    setItems([]);
    setMemoryKv({});
    setCoords({ lat: 37.775593, lng: -122.418137 });
    setMarker(null);
    processedItemsRef.current.clear(); // Limpiar el conjunto al desconectar

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();

    setIsPaused(true);

    // window.parent.postMessage('hideIframe', '*');
  }, []);

  const deleteConversationItem = useCallback(async (id: string) => {
    const client = clientRef.current;
    client.deleteItem(id);
  }, []);

  /**
   * In push-to-talk mode, start recording
   * .appendInputAudio() for each sample
   */
  const startRecording = async () => {
    setIsRecording(true);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const trackSampleOffset = await wavStreamPlayer.interrupt();
    if (trackSampleOffset?.trackId) {
      const { trackId, offset } = trackSampleOffset;
      await client.cancelResponse(trackId, offset);
    }
    await wavRecorder.record((data) => client.appendInputAudio(data.mono));
  };

  /**
   * In push-to-talk mode, stop recording
   */
  const stopRecording = async () => {
    setIsRecording(false);
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.pause();
    client.createResponse();
  };

  /**
   * Switch between Manual <> VAD mode for communication
   */
  const changeTurnEndType = async (value: string) => {
    try {
      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      if (value === 'none' && wavRecorder.getStatus() === 'recording') {
        await wavRecorder.pause();
      }
      client.updateSession({
        turn_detection: value === 'none' ? null : { type: 'server_vad' },
      });
      if (value === 'server_vad' && client.isConnected()) {
        await wavRecorder.record((data) => client.appendInputAudio(data.mono));
      }
      setCanPushToTalk(value === 'none');
    } catch (error) {
      console.log('Error en changeTurnEndType');
    }
  };

  const closeIframe = () => {
    window.parent.postMessage('hideIframe', '*');
  };

  /**
   * Auto-scroll the event logs
   */
  useEffect(() => {
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      const scrollHeight = eventsEl.scrollHeight;
      // Only scroll if height has just changed
      if (scrollHeight !== eventsScrollHeightRef.current) {
        eventsEl.scrollTop = scrollHeight;
        eventsScrollHeightRef.current = scrollHeight;
      }
    }
  }, [realtimeEvents]);

  /**
   * Auto-scroll the conversation logs
   */
  useEffect(() => {
    const conversationEls = [].slice.call(
      document.body.querySelectorAll('[data-conversation-content]')
    );
    for (const el of conversationEls) {
      const conversationEl = el as HTMLDivElement;
      conversationEl.scrollTop = conversationEl.scrollHeight;
    }
  }, [items]);

  /**
   * Set up render loops for the visualization canvas
   */
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;
    const clientCanvas = clientCanvasRef.current;
    let clientCtx: CanvasRenderingContext2D | null = null;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx: CanvasRenderingContext2D | null = null;

    const render = () => {
      if (isLoaded) {
        if (clientCanvas) {
          if (!clientCanvas.width || !clientCanvas.height) {
            clientCanvas.width = clientCanvas.offsetWidth;
            clientCanvas.height = clientCanvas.offsetHeight;
          }
          clientCtx = clientCtx || clientCanvas.getContext('2d');
          if (clientCtx) {
            clientCtx.clearRect(0, 0, clientCanvas.width, clientCanvas.height);
            const result = wavRecorder.recording
              ? wavRecorder.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              clientCanvas,
              clientCtx,
              result.values,
              '#0099ff',
              10,
              0,
              8
            );
          }
        }
        if (serverCanvas) {
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext('2d');
          if (serverCtx) {
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies('voice')
              : { values: new Float32Array([0]) };
            WavRenderer.drawBars(
              serverCanvas,
              serverCtx,
              result.values,
              '#009900',
              10,
              0,
              8
            );
          }
        }
        window.requestAnimationFrame(render);
      }
    };
    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ voice: 'coral', instructions: instructions });
    // Set transcription, otherwise we don't get user transcriptions back
    client.updateSession({ input_audio_transcription: { model: 'whisper-1' } });

    // Add tools
    client.addTool(
      {
        name: 'get_memory',
        description: 'Consulta la información almacenada en la memoria.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description: 'La clave del valor de la memoria a consultar.',
            },
          },
          required: ['key'],
        },
      },
      async ({ key }: { key: string }) => {
        const value = memoryKv[key] || null;
        console.log(`Consulta de memoria: ${key}=${value}`); // Log para depuración
        return value;
      }
    );

    client.addTool(
      {
        name: 'set_memory',
        description: 'Guarda la información importante del cliente en memoria.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description: 'La clave del valor de la memoria. Utilice siempre minúsculas y guiones bajos, ningún otro carácter.',
            },
            value: {
              type: 'string',
              description: 'El valor puede ser cualquier cosa representada como una cadena.',
            },
          },
          required: ['key', 'value'],
        },
      },
      async ({ key, value }: { [key: string]: any }) => {
        setMemoryKv((memoryKv) => {
          const newKv = { ...memoryKv };
          newKv[key] = value;
          console.log(`Memoria actualizada: ${key}=${value}`); // Log para depuración
          return newKv;
        });
        return { ok: true };
      }
    );

    // handle realtime events from client + server for event logging
    client.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          // if we receive multiple events in a row, aggregate them for display purposes
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client.on('error', (event: any) => console.error(event));
    client.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on('conversation.updated', async ({ item, delta }: any) => {
      const items = client.conversation.getItems();
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
      if (item.status === 'completed' && item.formatted.audio?.length) {
        const wavFile = await WavRecorder.decode(item.formatted.audio, 24000, 24000);
        item.formatted.file = wavFile;
      }
      setItems(items);

      // // Enviar respuesta a la API solo cuando la intervención del asistente esté completa
      // if (
      //   item.role === 'assistant' &&
      //   item.status === 'completed' &&
      //   (item.formatted.transcript || item.formatted.text) &&
      //   !processedItemsRef.current.has(item.id) // Evitar envíos duplicados
      // ) {
      //   processedItemsRef.current.add(item.id); // Marcar el ítem como procesado
      //   const textResponse = item.formatted.transcript || item.formatted.text;
      //   try {
      //     await axios.post('https://image-hub-ecommerce-sales-575353141558.us-east1.run.app/api/verify-conversation', {
      //       prompt: textResponse,
      //       // conversationId: item.id,
      //       // timestamp: new Date().toISOString(),
      //     }, {
      //       headers: { 'Content-Type': 'application/json' }
      //     });
      //     console.log('Respuesta enviada a la API:', textResponse);
      //   } catch (error) {
      //     console.error('Error al enviar la respuesta a la API:', error);
      //   }
      // }
    });

    setItems(client.conversation.getItems());

    return () => {
      // cleanup; resets to defaults
      client.reset();
      processedItemsRef.current.clear();
    };
  }, []);

  /**
   * Render the application
   */
  return (
    <div data-component="ConsolePage">
      <div className="content-main">
        <div className="content-logs">
          <div className="content-block conversation">
            <div className="content-block-title" style={{ backgroundColor: 'white', paddingBottom: '14px', borderBottom: '1px solid rgb(230, 230, 230)' }}>
              Conversación...
            </div>
            <div className="content-block-body" data-conversation-content style={{ marginBottom: '50px' }}>
              {!items.length && `esperando conexión...`}
              {items.map((conversationItem, i) => {
                return (
                  <div className="conversation-item" key={conversationItem.id}>
                    <div className={`speaker ${conversationItem.role || ''}`}>
                      <div>{(conversationItem.role || conversationItem.type).replaceAll('_', ' ')}</div>
                      <div className="close" onClick={() => deleteConversationItem(conversationItem.id)}>
                        <X />
                      </div>
                    </div>
                    <div className={`speaker-content`}>
                      {/* tool response */}
                      {conversationItem.type === 'function_call_output' && (
                        <div>{conversationItem.formatted.output}</div>
                      )}
                      {/* tool call */}
                      {!!conversationItem.formatted.tool && (
                        <div>{conversationItem.formatted.tool.name}({conversationItem.formatted.tool.arguments})</div>
                      )}
                      {!conversationItem.formatted.tool && conversationItem.role === 'user' && (
                        <div>
                          {conversationItem.formatted.transcript ||
                            (conversationItem.formatted.audio?.length
                              ? '(awaiting transcript)'
                              : conversationItem.formatted.text || '(item sent)')}
                        </div>
                      )}
                      {!conversationItem.formatted.tool && conversationItem.role === 'assistant' && (
                        <div>{conversationItem.formatted.transcript || conversationItem.formatted.text || '(truncated)'}</div>
                      )}
                      {conversationItem.formatted.file && (
                        <audio src={conversationItem.formatted.file.url} controls />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="content-actions flex justify-center fixed">
            {/* <Toggle
              defaultValue={false}
              labels={['manual', 'auto']} //auto=vad(voice activity detection)
              values={['none', 'server_vad']}
              onChange={(_, value) => changeTurnEndType(value)}
            /> */}
            {/* <div className="spacer" /> */}
            {/* {isConnected && canPushToTalk && (
              <Button
                label={isRecording ? 'release to send' : 'push to talk'}
                buttonStyle={isRecording ? 'alert' : 'regular'}
                disabled={!isConnected || !canPushToTalk}
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
              />
            )} */}
            {/* <div className="spacer" /> */}
            {/* <Button
              label={isConnected ? 'Detener' : 'Iniciar'}
              iconPosition={isConnected ? 'end' : 'start'}
              icon={isConnected ? X : Zap}
              buttonStyle={isConnected ? 'regular' : 'action'}
              onClick={isConnected ? disconnectConversation : connectConversation}
            /> */}
          </div>

          <div style={{ position: 'fixed', zIndex: 2, bottom: 15, width: '100%', backgroundColor: 'white', paddingTop: '14px', borderTop: '1px solid rgb(230, 230, 230)' }}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1, gap: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isPaused ? '#ff0927' : '#20eb73',
                  padding: '12px 20px',
                  borderRadius: '50px',
                  cursor: 'pointer',
                  color: 'white',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                  transition: 'background-color 0.3s ease',
                  zIndex: 2,
                  gap: '10px',
                  fontWeight: 'bold',
                  fontSize: '16px',
                }}
                onClick={togglePauseConversation}
              >
                {isPaused ? <MicOff size={20} /> : <Mic size={20} />}
                {/* {isPaused ? 'Reanudar' : 'Pausar'} */}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#12284C',
                  padding: '10px 20px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  color: 'white',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 2,
                }}
                onClick={isConnected ? disconnectConversation : connectConversation}
              >
                {isConnected ? (
                  <>
                    <img
                      src="https://androide.tech/assets/isotipo-amapola.svg"
                      alt="Cerrar"
                      style={{ width: '20px', height: '20px', marginRight: '8px' }}
                    />
                    <span>Detener</span>
                  </>
                ) : (
                  <>
                    <span>Iniciar</span>
                    <img
                      src="https://androide.tech/assets/isotipo-amapola.svg"
                      alt="Iniciar"
                      style={{ width: '20px', height: '20px', marginLeft: '8px' }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          <span
            style={{ position: 'fixed', zIndex: 2, top: 5, right: 5, backgroundColor: '#12284C', color: 'white', textAlign: 'center', cursor: 'pointer', borderRadius: '100%', padding: '5px 9px 5px 9px', fontSize: '15px' }}
            onClick={closeIframe}
          >
            ✕
          </span>
        </div>
      </div>
    </div>
  );
}